import { Color, Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, FC, useCallback } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import { formatDisplayValue } from 'dashboard/components/JsonSchemaSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import ForEachSettings from 'dashboard/components/WorkflowControlPanel/StepConfigLoop/ForEachSettings';
import MapSettings from 'dashboard/components/WorkflowControlPanel/StepConfigLoop/MapSettings';
import WhileSettings from 'dashboard/components/WorkflowControlPanel/StepConfigLoop/WhileSettings';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import {
  EMPTY_LOGIC_BLOCK_OR_V2,
  UsableObjectConfig,
  valueTransformer,
} from 'dashboard/lib/workflows';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError, WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowRunStepLoop } from 'dashboard/models/workflow-step';
import {
  FakeWorkflowStepType,
  newPlaceholderStep,
  WorkflowStep,
  WorkflowStepLoop,
  WorkflowStepLoopModeType,
  WorkflowStepLoopType,
  WorkflowStepLoopWhileSettings,
} from 'dashboard/models/workflow-version';
import { useSchemaForFieldPath } from 'dashboard/redux/selectors/workflow-editor-context';
import Button from 'design-system/components/Button';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import { JsonSchema, JsonSchemaArray } from 'lib/json-schema';
import { buildGraph, generateWorkflowToken } from 'lib/workflows';

const BranchesRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing[2]};
`;

const OutputContainer = styled.pre`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]};
  height: 150px;
  overflow: auto;
`;

const SmallLoadingIndicator = styled(LoadingIndicator)`
  height: ${Spacing[5]};
  width: ${Spacing[5]};
  margin-top: -${Spacing[2]};
  margin-bottom: -${Spacing[2]};
`;

const LOOP_TYPE_TO_DEFAULT_STEP_NAME: Record<WorkflowStepLoopType, string> = {
  [WorkflowStepLoopType.ForEach]: 'Loop Step',
  [WorkflowStepLoopType.While]: 'Loop Step',
  [WorkflowStepLoopType.Map]: 'Array Map Step',
};

interface WorkflowConfigurationLoopProps {
  cycleIdx?: number;
  editable: boolean;
  errors: WorkflowError[];
  jsonSchema: JsonSchema;
  step: WorkflowStepLoop;
  stepsConfig: WorkflowStep[];
  tokensToDisplayValue: Record<string, string>;
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  workflowRunStep?: WorkflowRunStepLoop;
  workflowSchema: WorkflowSchema;
  isLoadingCycle: boolean;
  updateCycleIndex?: (idx: number, token: string) => void;
  updateStep: (newStep: WorkflowStep) => void;
}

const StepConfigLoop: FC<WorkflowConfigurationLoopProps> = ({
  cycleIdx = 0,
  editable,
  errors,
  jsonSchema,
  step,
  stepsConfig,
  tokensToDisplayValue,
  trigger,
  usableObjects,
  workflowRunStep,
  workflowSchema,
  isLoadingCycle,
  updateCycleIndex,
  updateStep,
}) => {
  const schemaForFieldPath = useSchemaForFieldPath();
  const { renderErrorMessageFor } = useWorkflowError<'branch'>(errors);
  const tokensToSteps = buildGraph(stepsConfig);
  const onSelectDropdown = (idx: string) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    updateCycleIndex(parseInt(idx), step.token);
  };
  const handleAdd = () => {
    updateStep({
      ...step,
      branch: [...step.branch, newPlaceholderStep()],
    });
  };
  const updateSettingsType = (newType: WorkflowStepLoopType) => {
    const branch =
      step.settings.type === WorkflowStepLoopType.Map &&
      [WorkflowStepLoopType.ForEach, WorkflowStepLoopType.While].includes(newType)
        ? step.branch.filter(
            (childStep) =>
              !(
                step.settings.type === WorkflowStepLoopType.Map &&
                childStep.token === step.settings['output-step-token']
              ),
          )
        : step.branch;
    const name =
      step.name === LOOP_TYPE_TO_DEFAULT_STEP_NAME[step.settings.type]
        ? LOOP_TYPE_TO_DEFAULT_STEP_NAME[newType]
        : step.name;

    switch (newType) {
      case WorkflowStepLoopType.ForEach:
        return updateStep({
          ...step,
          name,
          branch,
          settings: {
            type: newType,
            'array-path': '',
            mode: WorkflowStepLoopModeType.SEQUENTIAL,
          },
        });
      case WorkflowStepLoopType.While:
        return updateStep({
          ...step,
          name,
          branch,
          settings: {
            type: newType,
            conditional: {
              token: generateWorkflowToken(),
              name: '',
              rules: EMPTY_LOGIC_BLOCK_OR_V2,
            },
            'max-iterations': 3,
          },
        });
      case WorkflowStepLoopType.Map: {
        const outputStep = {
          type: FakeWorkflowStepType.MapOutput,
          token: `${step.token}-map-output`,
          name: 'Array Map Output',
        };
        return updateStep({
          ...step,
          name,
          branch: [...step.branch, outputStep],
          settings: {
            type: newType,
            'array-path': '',
            'item-path': '',
            'output-step-token': outputStep.token,
            mode: WorkflowStepLoopModeType.SEQUENTIAL,
          },
        });
      }
    }
  };
  const updateInputArraySettings = useCallback(
    (path: string) => {
      if ('array-path' in step.settings) {
        const valueFormatter = valueTransformer(tokensToDisplayValue).getDisplayValue;
        updateStep({
          ...step,
          settings: {
            ...step.settings,
            'array-path': path,
            'input-array-schema': schemaForFieldPath(valueFormatter(path), jsonSchema)
              .schema as JsonSchemaArray,
          },
        });
      }
    },
    [step, tokensToDisplayValue, updateStep, schemaForFieldPath, jsonSchema],
  );
  const updateMode = (value: WorkflowStepLoopModeType) => {
    if ('mode' in step.settings) {
      return updateStep({
        ...step,
        settings: {
          ...step.settings,
          mode: value,
        },
      });
    }
  };
  const onChangeMaxIterations = (event: ChangeEvent<HTMLInputElement>) => {
    updateStep({
      ...step,
      settings: {
        ...(step.settings as WorkflowStepLoopWhileSettings),
        'max-iterations': parseInt(event.target.value),
      },
    });
  };

  return (
    <>
      <BranchesRow>
        <Paragraph size='md'>{step.branch.length} steps</Paragraph>
        <Button
          id='step-config-loop__new-step-button'
          type='button'
          variant='basic'
          width='auto'
          onClick={handleAdd}
          disabled={!editable}
        >
          New Step
        </Button>
      </BranchesRow>
      <Spacer height={1} />
      <ParamLabel htmlFor='step-config-loop__select-type' labelText='Loop type' />
      <Select
        disabled={!editable}
        id='step-config-loop__select-type'
        value={step.settings.type ?? ''}
        onValueChange={updateSettingsType}
        placeholder='Select loop type'
      >
        <Option value={WorkflowStepLoopType.ForEach}>For Each</Option>
        <Option value={WorkflowStepLoopType.While}>While</Option>
        <Option value={WorkflowStepLoopType.Map}>Map</Option>
      </Select>
      <Spacer height={2} />
      {workflowRunStep && (
        <>
          <ParamLabel labelText='Cycle through iterations' htmlFor='step-config-loop__iteration' />
          <HStack>
            <Select
              id='step-config-loop__iteration'
              disabled={isLoadingCycle}
              value={cycleIdx.toString()}
              onValueChange={onSelectDropdown}
            >
              {[...Array(workflowRunStep.attributes['current-cycle'] + 1).keys()].map(
                (item: number) => (
                  <Option key={item} value={item.toString()}>
                    {item + 1}
                  </Option>
                ),
              )}
            </Select>
            {isLoadingCycle && <SmallLoadingIndicator />}
          </HStack>
          <Spacer height={2} />
        </>
      )}
      {step.settings.type === WorkflowStepLoopType.ForEach && (
        <>
          {workflowRunStep && (
            <>
              <ParamLabel labelText='Input array' htmlFor='step-config-loop__input-array' />
              <OutputContainer>
                {formatDisplayValue(workflowRunStep.attributes['resolved-input-array'])}
              </OutputContainer>
              <Spacer height={2} />
            </>
          )}
          <ForEachSettings
            step={step}
            updateStep={updateStep}
            editable={editable}
            errors={errors}
            trigger={trigger}
            jsonSchema={jsonSchema}
            tokensToDisplayValue={tokensToDisplayValue}
            settings={step.settings}
            updateForEachSettings={updateInputArraySettings}
            updateMode={updateMode}
          />
        </>
      )}
      {step.settings.type === WorkflowStepLoopType.While && (
        <WhileSettings
          settings={step.settings}
          step={step}
          editable={editable}
          errors={errors}
          trigger={trigger}
          workflowSchema={workflowSchema}
          tokensToSteps={tokensToSteps}
          usableObjects={usableObjects}
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          onChangeMaxIterations={onChangeMaxIterations}
          updateStep={updateStep}
        />
      )}
      {step.settings.type === WorkflowStepLoopType.Map && (
        <>
          {workflowRunStep && (
            <>
              <ParamLabel labelText='Input array' htmlFor='step-config-loop__input-array' />
              <OutputContainer>
                {formatDisplayValue(workflowRunStep.attributes['resolved-input-array'])}
              </OutputContainer>
              <Spacer height={2} />
            </>
          )}
          <MapSettings
            step={step}
            updateStep={updateStep}
            editable={editable}
            errors={errors}
            trigger={trigger}
            jsonSchema={jsonSchema}
            tokensToDisplayValue={tokensToDisplayValue}
            settings={step.settings}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            workflowRunStep={workflowRunStep}
            updateMapSettings={updateInputArraySettings}
            updateMode={updateMode}
          />
        </>
      )}
      {renderErrorMessageFor('branch')}
    </>
  );
};

export default StepConfigLoop;
